import type { LucideIcon } from 'lucide-react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/cn';

/**
 * A KPI tile: icon, label, value, delta, sparkline.
 *
 * `invertDelta` exists because a rising number is not always good news — a
 * refund count going up is the obvious case, and colouring it green would be
 * actively misleading.
 *
 * The sparkline has no axes, no tooltip and no legend. It shows shape, not
 * values; the value is the number beside it.
 */
export function StatTile({
  label,
  value,
  delta,
  icon: Icon,
  series = [],
  invertDelta = false,
}: {
  label: string;
  value: string;
  delta?: number | null;
  icon: LucideIcon;
  series?: number[];
  invertDelta?: boolean;
}) {
  const isGood = delta === null || delta === undefined ? null : invertDelta ? delta <= 0 : delta >= 0;

  const data = series.map((v, i) => ({ i, v }));
  const strokeColor = isGood === false ? 'var(--color-critical)' : 'var(--color-brand)';

  return (
    <div className="card flex items-center gap-4 p-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand">
        <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-ink-muted">{label}</p>
        <p className="mt-0.5 text-[28px] font-semibold leading-none tracking-tight">{value}</p>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5">
        {delta !== null && delta !== undefined && (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium',
              isGood ? 'bg-good-subtle text-[#006300]' : 'bg-critical-subtle text-critical',
            )}
          >
            {delta >= 0 ? (
              <TrendingUp className="h-3 w-3" strokeWidth={2.5} aria-hidden />
            ) : (
              <TrendingDown className="h-3 w-3" strokeWidth={2.5} aria-hidden />
            )}
            {delta >= 0 ? '+' : ''}
            {delta.toFixed(1)}%
          </span>
        )}

        {data.length > 1 && (
          <div className="h-8 w-16" aria-hidden>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={strokeColor} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={strokeColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={strokeColor}
                  strokeWidth={2}
                  fill={`url(#spark-${label})`}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
